import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleUser, Settings } from 'lucide-react'
import { nodeState, toggleNode, type SelectionNode } from '@/src/renderer/lib/backup/selection'
import { ProjectIcon } from '@/src/renderer/components/common/ProjectIcon/ProjectIcon'
import { LifeSpaceColorIcon } from '@/src/renderer/components/icons/LifeSpaceColorIcon'
import { QuickNotesEmptyIcon, QuickNotesFilledIcon } from '@/src/renderer/components/icons'
import { GoalsIcon } from '@/src/renderer/components/icons/GoalsIcon'
import { SproutIcon } from '@/src/renderer/components/icons/SproutIcon'
import './BackupSelectionTree.scss'

interface BackupSelectionTreeProps {
  tree: readonly SelectionNode[]
  /** Die gewählten Blätter (`project:<id>`, `quickNotes:<bereich>`, `settings` …). */
  value: ReadonlySet<string>
  onValueChange: (next: Set<string>) => void
  disabled?: boolean
  /** Knoten, die es gibt, die hier aber nicht wählbar sind — mit Hinweis (Markdown: Einstellungen, Profil). */
  lockedKeys?: ReadonlyMap<string, string>
  /** Hinweis an Knoten, die es nicht gibt („Nicht in diesem Backup“). */
  unavailableHint?: string
}

/**
 * **Was mitgeht** (#158, Entscheidung 1): Lebensbereich → Projekte, „Kurz notiert“, Ziele,
 * Gewohnheiten; dazu Einstellungen (und beim Export das Profil). Ein Haken am Lebensbereich nimmt
 * alles darunter mit; teilweise gewählt zeigt der Haken einen Strich.
 *
 * @example
 * <BackupSelectionTree tree={tree} value={selected} onValueChange={setSelected} />
 */
export function BackupSelectionTree({ tree, value, onValueChange, disabled = false, lockedKeys, unavailableHint }: BackupSelectionTreeProps) {
  return (
    <ul className="backup-tree">
      {tree.map(node => (
        <TreeNode
          key={node.key}
          node={node}
          value={value}
          onValueChange={onValueChange}
          disabled={disabled}
          lockedKeys={lockedKeys}
          unavailableHint={unavailableHint}
        />
      ))}
    </ul>
  )
}

function TreeNode({ node, value, onValueChange, disabled, lockedKeys, unavailableHint }: Omit<BackupSelectionTreeProps, 'tree'> & { node: SelectionNode }) {
  const { t } = useTranslation()
  const box = useRef<HTMLInputElement>(null)
  const lockedHint = lockedKeys?.get(node.key)
  const selectable = !lockedHint && (node.available || node.children.some(child => child.available))
  const state = selectable ? nodeState(node, value) : 'unchecked'

  useEffect(() => {
    if (box.current) box.current.indeterminate = state === 'partial'
  }, [state])

  const label = node.kind === 'project' ? node.name
    : node.kind === 'lifeSpace' ? t(`lifeSpaces.${node.lifeSpace}`)
      : t(`preferences.backups.tree.${node.kind}`)
  const hint = lockedHint ?? (!selectable && unavailableHint ? unavailableHint : null)
  const id = `backup-tree-${node.key}`

  return (
    <li className={`backup-tree__node backup-tree__node--${node.kind}`}>
      <div className={`backup-tree__row${selectable ? '' : ' backup-tree__row--off'}`}>
        <input
          ref={box}
          id={id}
          type="checkbox"
          className="backup-tree__box"
          checked={state === 'checked'}
          disabled={disabled || !selectable}
          onChange={() => onValueChange(toggleNode(node, value))}
        />
        <span className="backup-tree__icon" aria-hidden="true"><NodeIcon node={node} /></span>
        <label className="backup-tree__label" htmlFor={id}>{label}</label>
        {selectable && node.count > 0 && <span className="backup-tree__count">{node.count}</span>}
        {hint && <span className="backup-tree__hint">{hint}</span>}
      </div>
      {node.children.length > 0 && (
        <ul className="backup-tree__children">
          {node.children.map(child => (
            <TreeNode
              key={child.key}
              node={child}
              value={value}
              onValueChange={onValueChange}
              disabled={disabled}
              lockedKeys={lockedKeys}
              unavailableHint={unavailableHint}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

/** Dieselben Zeichen wie in der Sidebar: Lebensbereich farbig, Projekt mit seinem Icon. */
function NodeIcon({ node }: { node: SelectionNode }) {
  switch (node.kind) {
    case 'lifeSpace':
      return <LifeSpaceColorIcon lifeSpace={node.lifeSpace ?? ''} size={16} />
    case 'project':
      return <ProjectIcon icon={node.icon} iconColor={node.iconColor} size={16} />
    case 'quickNotes':
      return node.count > 0 ? <QuickNotesFilledIcon size={16} /> : <QuickNotesEmptyIcon size={16} />
    case 'goals':
      return <GoalsIcon size={16} />
    case 'habits':
      return <SproutIcon size={16} />
    case 'settings':
      return <Settings size={16} strokeWidth={1.75} />
    case 'profile':
      return <CircleUser size={16} strokeWidth={1.75} />
  }
}
